/* eslint-disable lingui/no-unlocalized-strings */
import * as Sentry from '@sentry/react';

export type McpChartFailureStage =
  | 'not_chartable'
  | 'bundle_not_found'
  | 'bundle_auth_error'
  | 'bundle_fetch_error'
  | 'bundle_empty'
  | 'invalid_resource_uri'
  | 'mount_failed'
  | 'teardown_failed'
  | 'app_log_error'
  | 'app_log_warning';

type ReportMcpChartFailureParams = {
  stage: McpChartFailureStage;
  resourceUri?: string;
  toolName?: string;
  error?: unknown;
  extra?: Record<string, unknown>;
};

const OBSERVABLE_STAGES = new Set<McpChartFailureStage>([
  'bundle_not_found',
  'bundle_auth_error',
  'bundle_fetch_error',
  'bundle_empty',
  'invalid_resource_uri',
  'mount_failed',
  'teardown_failed',
  'app_log_error',
  'app_log_warning',
]);

const getSentryTags = ({
  stage,
  resourceUri,
  toolName,
}: Pick<ReportMcpChartFailureParams, 'stage' | 'resourceUri' | 'toolName'>) => ({
  'mcp:chartStage': stage,
  ...(resourceUri ? { 'mcp:resourceUri': resourceUri } : {}),
  ...(toolName ? { 'mcp:toolName': toolName } : {}),
});

export const reportMcpChartFailure = ({
  stage,
  resourceUri,
  toolName,
  error,
  extra,
}: ReportMcpChartFailureParams): void => {
  Sentry.addBreadcrumb({
    type: 'default',
    category: 'MCP Chart',
    message: stage,
    data: { resourceUri, toolName, ...extra },
    level: stage === 'not_chartable' ? 'info' : 'warning',
  });

  if (!OBSERVABLE_STAGES.has(stage)) {
    return;
  }

  const tags = getSentryTags({ stage, resourceUri, toolName });
  const failureExtra = {
    message: `MCP chart failure: ${stage}`,
    ...extra,
  };

  if (error instanceof Error) {
    Sentry.captureException(error, { tags, extra: failureExtra });
    return;
  }

  Sentry.captureMessage(`MCP chart failure: ${stage}`, {
    level: stage === 'app_log_warning' ? 'warning' : 'error',
    tags,
    extra: failureExtra,
  });
};

export const reportMcpChartAppLog = ({
  level,
  logger,
  data,
  resourceUri,
  toolName,
}: {
  level: string;
  logger?: string;
  data: unknown;
  resourceUri: string;
  toolName: string;
}): void => {
  const isErrorLevel =
    level === 'error' || level === 'critical' || level === 'alert' || level === 'emergency';
  const isWarningLevel = level === 'warning';

  if (!isErrorLevel && !isWarningLevel) {
    return;
  }

  reportMcpChartFailure({
    stage: isErrorLevel ? 'app_log_error' : 'app_log_warning',
    resourceUri,
    toolName,
    extra: { level, logger, data },
  });
};
